import { api } from '@/lib/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bot, History, User as UserIcon } from 'lucide-react';

const VIA_LABEL: Record<string, string> = {
  'llm-ingest': '자료 정리',
  'llm-health': '건강검진',
  manual: '수동 편집',
};

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}초 전`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}일 전`;
  return d.toLocaleDateString('ko-KR');
}

export function WikiLog() {
  const query = useInfiniteQuery({
    queryKey: ['wiki', 'log'],
    queryFn: ({ pageParam }) => api.wikiLog(pageParam, 30),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30_000,
  });

  if (query.isLoading) {
    return <div className="p-8 text-foreground-muted text-sm">불러오는 중…</div>;
  }
  if (query.isError) {
    return (
      <div className="p-8 text-destructive text-sm">
        작업 이력을 불러오지 못했습니다: {(query.error as Error).message}
      </div>
    );
  }

  const entries = query.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <header className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
          <History className="size-7" /> 작업 이력
        </h1>
        <p className="mt-2 text-foreground-muted">
          위키 페이지의 모든 변경 기록입니다. LLM 자료 정리/건강검진과 사람의 수동 편집을 모두
          포함합니다.
        </p>
      </header>

      {entries.length === 0 ? (
        <div className="rounded-md border border-border bg-surface p-8 text-center text-foreground-muted">
          아직 작업 이력이 없습니다.
        </div>
      ) : (
        <ul className="space-y-3">
          {entries.map((e) => (
            <li
              key={e.revisionId}
              className={`rounded-md border bg-surface p-3 transition-colors ${
                e.pageRemoved ? 'border-border opacity-60' : 'border-border hover:border-accent'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    {e.editedVia.startsWith('llm') ? (
                      <Bot className="size-4 text-accent" />
                    ) : (
                      <UserIcon className="size-4 text-foreground-muted" />
                    )}
                    <span className="font-medium">{e.editorName ?? '시스템'}</span>
                    <span className="text-foreground-faint">·</span>
                    <span className="text-xs text-foreground-muted">
                      {VIA_LABEL[e.editedVia] ?? e.editedVia}
                    </span>
                    {e.pageRemoved ? (
                      <span className="ml-2 rounded bg-destructive/10 px-1.5 py-0.5 text-xs text-destructive">
                        삭제됨
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1">
                    {e.pageRemoved ? (
                      <span className="font-medium text-foreground-muted">{e.pageTitle}</span>
                    ) : (
                      <Link
                        to="/wikis/p/$slug"
                        params={{ slug: e.pageSlug }}
                        className="font-medium text-foreground hover:text-accent"
                      >
                        {e.pageTitle}
                      </Link>
                    )}
                    <code className="ml-2 rounded bg-code-bg px-1 py-0.5 text-xs text-foreground-faint">
                      {e.pageSlug}
                    </code>
                  </div>
                  {e.takeawayPreview ? (
                    <p className="mt-1 text-sm text-foreground-muted line-clamp-2">
                      {e.takeawayPreview}
                    </p>
                  ) : null}
                  {e.note ? (
                    <p className="mt-1 text-xs text-foreground-faint italic">{e.note}</p>
                  ) : null}
                </div>
                <span className="shrink-0 text-xs text-foreground-faint" title={e.createdAt}>
                  {formatRelative(e.createdAt)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {query.hasNextPage ? (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="rounded-md border border-border bg-surface px-4 py-2 text-sm hover:border-accent disabled:opacity-50"
          >
            {query.isFetchingNextPage ? '불러오는 중…' : '더 보기'}
          </button>
        </div>
      ) : entries.length > 0 ? (
        <p className="mt-6 text-center text-xs text-foreground-faint">— 끝 —</p>
      ) : null}
    </div>
  );
}
